<script setup lang="ts">
import type { PostTypeSeo } from '~~/server/types/generated';

/* ----------------------------------------------
   Types
---------------------------------------------- */
interface EventTaxonomyNode {
  id: string;
  name: string;
  slug: string;
}

interface EventTaxonomyConnection {
  nodes: EventTaxonomyNode[];
}

interface CptCampusEvents {
  allDayEvent?: boolean;
  eventDateStart?: string;
  eventDateEnd?: string;
  eventDescription?: string;
  eventRegistrationLink?: string | null;
  eventTimeStart?: string;
  eventTimeEnd?: string;
  eventType?: EventTaxonomyConnection;
  eventFormat?: EventTaxonomyConnection;
  eventLocationName?: string;
  eventAddress1?: string;
  eventAddress2?: string | null;
  eventCity?: string;
  eventState?: string;
  eventZip?: string;
}

interface CampusEvent {
  id: string;
  title: string;
  slug: string;
  uri?: string;
  cptCampusEvents?: CptCampusEvents;
  eventGoogleMapsLink?: string;
}

interface ImportantAnnouncementItem {
  id: string;
  title: string;
  slug: string;
  uri?: string;
  type: 'Academic' | 'Event';
  eventDateStart?: string | null;
  eventDescription?: string | null;
}

interface ImportantAnnouncementsModule {
  importantAnnouncements: boolean;
  importantAnnouncementsItems: ImportantAnnouncementItem[];
}

interface CampusEventsHub {
  seo?: PostTypeSeo | null;
  heroHeading?: string;
  heroTeaser?: string;
  heroAlternateCalendarButton?: {
    __typename?: string;
    title?: string;
    uri?: string;
  };
  importantAnnouncementsModule?: ImportantAnnouncementsModule | null;
}

interface CampusEventsApiResponse {
  hub: CampusEventsHub | null;
  events: CampusEvent[];
}

/* ----------------------------------------------
   API FETCH
---------------------------------------------- */
const { data, error, pending } = await useAsyncData('campus-events-hub', async (nuxtApp) => {
  const url = `/api/campus-events/`;
  const response = await $fetch(url);
  return response as CampusEventsApiResponse;
});

const hub = computed(() => data.value?.hub ?? null);
const events = computed(() => data.value?.events ?? []);

if (hub.value?.seo) {
  useBellevueSEO(hub.value.seo);
}

/* ----------------------------------------------
   Sort events ASCENDING by start date
---------------------------------------------- */
const sortedEvents = computed(() => {
  return [...events.value].sort((a, b) => {
    const aDate = parseCMSDate(a.cptCampusEvents?.eventDateStart)?.getTime() ?? 0;
    const bDate = parseCMSDate(b.cptCampusEvents?.eventDateStart)?.getTime() ?? 0;
    return aDate - bDate;
  });
});

/* ----------------------------------------------
   Remove past events (show only today + future)
---------------------------------------------- */
const today = useToday();

const sortedFutureEvents = computed(() => {
  return sortedEvents.value.filter((event) => {
    const start = parseCMSDate(event.cptCampusEvents?.eventDateStart);
    if (!start) return false;
    return start >= today.value;
  });
});

/* ----------------------------------------------
   Date Helpers
---------------------------------------------- */
const parseCMSDate = (dateStr?: string) => {
  if (!dateStr) return null;

  // Extract YYYY-MM-DD from any ISO format
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;

  const [_, y, m, d] = match;
  return new Date(Number(y), Number(m) - 1, Number(d)); // LOCAL DATE, no timezone shift
};

const formatMonthYear = (dateStr?: string) => {
  const d = parseCMSDate(dateStr);
  return d ? d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '';
};

const formatDay = (dateStr?: string) => {
  const d = parseCMSDate(dateStr);
  return d ? d.getDate().toString().padStart(2, '0') : '';
};

const formatWeekday = (dateStr?: string) => {
  const d = parseCMSDate(dateStr);
  return d ? d.toLocaleDateString('en-US', { weekday: 'long' }) : '';
};

const formatMonthOption = (date: Date) =>
  date.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });

/* ----------------------------------------------
   Filters
---------------------------------------------- */
const selectedMonth = ref<string>('');
const selectedType = ref<string>(''); // future use
const isMonthDropdownOpen = ref(false);
const isTypeDropdownOpen = ref(false);

const MONTH_FILTER_WINDOW = 6;

/* Available Months */
const availableMonths = computed(() => {
  const start = new Date(today.value.getFullYear(), today.value.getMonth(), 1);

  return Array.from({ length: MONTH_FILTER_WINDOW }, (_, offset) => {
    const monthDate = new Date(start.getFullYear(), start.getMonth() + offset, 1);
    return formatMonthOption(monthDate);
  });
});

/* Filtered Events */
const filteredEvents = computed(() => {
  return sortedFutureEvents.value.filter((event) => {
    const date = event.cptCampusEvents?.eventDateStart;

    const typeNodes = event.cptCampusEvents?.eventType?.nodes ?? [];

    let matchesMonth = true;
    let matchesType = true;

    if (selectedMonth.value) {
      const parsedDate = parseCMSDate(date);
      const eventMonth = parsedDate ? formatMonthOption(parsedDate) : '';
      matchesMonth = eventMonth === selectedMonth.value;
    }

    if (selectedType.value) {
      matchesType = typeNodes.some((term) => term.slug === selectedType.value);
    }

    return matchesMonth && matchesType;
  });
});

const hasResults = computed(() => filteredEvents.value.length > 0);

watch(availableMonths, (months) => {
  if (!selectedMonth.value) return;
  if (months.includes(selectedMonth.value)) return;

  selectedMonth.value = '';
});

// Available Event Types (from ACF dropdown)
const availableEventTypes = computed(() => {
  const map = new Map<string, { name: string; slug: string }>();

  sortedFutureEvents.value.forEach((event) => {
    const nodes = event.cptCampusEvents?.eventType?.nodes;

    nodes?.forEach((type) => {
      map.set(type.slug, type);
    });
  });

  return Array.from(map.values());
});
const selectedTypeLabel = computed(() => {
  if (!selectedType.value) return '';
  return availableEventTypes.value.find((t) => t.slug === selectedType.value)?.name || '';
});

/* ----------------------------------------------
   Close Dropdowns on Outside Click + Mutual Close
---------------------------------------------- */
const monthDropdownRef = ref<HTMLElement | null>(null);
const typeDropdownRef = ref<HTMLElement | null>(null);

// Close other dropdown when one opens
watch(isMonthDropdownOpen, (open) => {
  if (open) isTypeDropdownOpen.value = false;
});

watch(isTypeDropdownOpen, (open) => {
  if (open) isMonthDropdownOpen.value = false;
});

// Close when clicking outside
const handleDocumentClick = (e: MouseEvent) => {
  const target = e.target as Node;

  if (monthDropdownRef.value?.contains(target) || typeDropdownRef.value?.contains(target)) {
    return;
  }

  isMonthDropdownOpen.value = false;
  isTypeDropdownOpen.value = false;
};

onMounted(() => {
  document.addEventListener('click', handleDocumentClick);
});

onUnmounted(() => {
  document.removeEventListener('click', handleDocumentClick);
});

/* ----------------------------------------------
   Clear Filters Button Logic
---------------------------------------------- */
const hasActiveFilters = computed(() => {
  return !!selectedMonth.value || !!selectedType.value;
});

const hasAnyUpcomingEvents = computed(() => {
  return sortedFutureEvents.value.length > 0;
});

const noResultsMessage = computed(() => {
  if (!hasAnyUpcomingEvents.value) {
    return 'Sorry, there are no events listed right now. Please check back later.';
  }

  return 'No events match your current filters. Please clear filters and try again.';
});

const showEmptyStateClearFilters = computed(() => {
  return hasActiveFilters.value && hasAnyUpcomingEvents.value;
});

const clearAllFilters = () => {
  selectedMonth.value = '';
  selectedType.value = '';
  isMonthDropdownOpen.value = false;
  isTypeDropdownOpen.value = false;
};

/* ----------------------------------------------
   Pagination (Load More)
---------------------------------------------- */
const eventsPerPage = 6;
const visibleCount = ref(eventsPerPage);

// Reset pagination when filters change
watch([selectedMonth, selectedType], () => {
  visibleCount.value = eventsPerPage;
});

// Slice filtered events
const paginatedEvents = computed(() => {
  return filteredEvents.value.slice(0, visibleCount.value);
});

// If more results exist
const hasMoreEvents = computed(() => {
  return visibleCount.value < filteredEvents.value.length;
});

// Load more function
const loadMore = () => {
  visibleCount.value += eventsPerPage;
};
</script>

<template>
  <div
    id="tuition"
    class="scroll-mt-15"
    style="
      background:
        url('/images/bg-right-pattern.svg') right no-repeat,
        linear-gradient(184deg, #fbf5e7 1.28%, #fff 94.01%);
      background-size: auto, auto;
      overflow: hidden;
      background-position: top right;
    "
  >
    <LayoutsContainer padding-top="larger">
      <!-- Page Hero -->
      <div class="flex flex-col flex-nowrap items-start gap-6 md:flex-row md:gap-32">
        <div class="w-full md:w-2/6">
          <TypographyTitle size="xl" as="h1" :html="hub?.heroHeading" />
        </div>

        <div class="w-full md:w-4/6">
          <div
            class="hero-teaser text-brand-600 mb-8 leading-[28px] font-medium md:text-xl md:leading-[26px] md:font-black md:-tracking-[0.4px]"
            v-html="hub?.heroTeaser"
          />

          <BaseButton
            v-if="hub?.heroAlternateCalendarButton"
            :to="hub.heroAlternateCalendarButton.uri"
            variant="primary"
          >
            {{ hub.heroAlternateCalendarButton.title }}
          </BaseButton>
        </div>
      </div>

      <BaseDivider class="max-md:mt-6 max-md:mb-10 md:my-16" />

      <ImportantAnnouncements :module="hub?.importantAnnouncementsModule" />

      <div class="mb-10 flex flex-col items-start max-md:gap-3.5 md:flex-row">
        <!-- Clear filtered results -->
        <BaseButton
          v-if="hasActiveFilters"
          variant="outline"
          class="!bg-accent-100 !border-accent-500 !rounded-lg border-1 !px-5 !py-2.5 font-medium !text-black max-md:order-last"
          @click="clearAllFilters"
        >
          Clear filters <span class="text-brand-500 ml-2.5">✕</span>
        </BaseButton>

        <div class="flex flex-col gap-3.5 md:flex-row">
          <div class="relative" ref="monthDropdownRef">
            <button
              class="border-accent-500 text-brand-900 hover:bg-brand-50 flex w-full min-w-[195px] items-center justify-between rounded-md border bg-white px-3 py-2.5 font-medium shadow-sm transition"
              @click="isMonthDropdownOpen = !isMonthDropdownOpen"
            >
              <span>{{ selectedMonth || 'Filter by month' }}</span>
              <svg
                :class="['h-4 w-4 transition-transform', isMonthDropdownOpen ? 'rotate-180' : '']"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            <ul
              v-if="isMonthDropdownOpen"
              class="border-brand-200 absolute right-0 left-0 z-20 mt-1 max-h-60 overflow-y-auto rounded-md border bg-white shadow-lg"
            >
              <li>
                <button
                  class="hover:bg-brand-50 text-brand-600 w-full px-4 py-2 text-left transition"
                  @click="
                    selectedMonth = '';
                    isMonthDropdownOpen = false;
                  "
                >
                  All Months
                </button>
              </li>

              <li v-for="month in availableMonths" :key="month">
                <button
                  class="hover:bg-brand-50 text-brand-600 w-full px-4 py-2 text-left transition"
                  @click="
                    selectedMonth = month;
                    isMonthDropdownOpen = false;
                  "
                >
                  {{ month }}
                </button>
              </li>
            </ul>
          </div>

          <div class="relative" ref="typeDropdownRef">
            <!-- Event Type Dropdown Trigger -->
            <button
              class="border-accent-500 text-brand-900 hover:bg-brand-50 flex w-full min-w-[230px] items-center justify-between rounded-md border bg-white px-3 py-2.5 font-medium shadow-sm transition"
              @click="isTypeDropdownOpen = !isTypeDropdownOpen"
            >
              <span>{{ selectedTypeLabel || 'Filter by event type' }}</span>

              <svg
                :class="['h-4 w-4 transition-transform', isTypeDropdownOpen ? 'rotate-180' : '']"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            <!-- Event Type Dropdown List -->
            <ul
              v-if="isTypeDropdownOpen"
              class="border-brand-200 absolute right-0 left-0 z-20 mt-1 max-h-60 overflow-y-auto rounded-md border bg-white shadow-lg"
            >
              <li>
                <button
                  class="hover:bg-brand-50 text-brand-600 w-full px-4 py-2 text-left transition"
                  @click="
                    selectedType = '';
                    isTypeDropdownOpen = false;
                  "
                >
                  All Types
                </button>
              </li>

              <li v-for="type in availableEventTypes" :key="type.slug">
                <button
                  class="hover:bg-brand-50 text-brand-600 w-full px-4 py-2 text-left transition"
                  @click="
                    selectedType = type.slug;
                    isTypeDropdownOpen = false;
                  "
                >
                  {{ type.name }}
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="flex">
        <!-- Events -->
        <div class="flex w-full flex-col gap-8">
          <!-- No results -->
          <div
            v-if="!filteredEvents.length"
            class="border-brand-300 bg-brand-50 rounded-2xl border border-dashed px-8 py-12 text-center"
          >
            <TypographyTitle size="sm" as="h5" :class="showEmptyStateClearFilters ? 'mb-4' : ''">
              {{ noResultsMessage }}
            </TypographyTitle>

            <BaseButton v-if="showEmptyStateClearFilters" variant="outline" @click="clearAllFilters">
              Clear Filters
            </BaseButton>
          </div>

          <div
            v-for="event in paginatedEvents"
            :key="event.id"
            class="mx-auto w-full rounded-2xl bg-white pt-4 pr-8 pb-8 pl-4 md:px-16 md:py-10"
            style="
              box-shadow:
                5px 5px 1px 0 rgba(219, 170, 49, 0.1),
                10px 15px 25px 0 rgba(220, 174, 58, 0.07);
            "
          >
            <div class="flex gap-4 md:items-stretch md:gap-8 lg:gap-16">
              <!-- Date -->
              <div class="">
                <div
                  class="text-brand-600 bg-brand-50 min-w-[78px] rounded-lg px-2 py-4 text-center text-xs font-medium md:min-w-[115px] md:rounded-2xl md:p-4 md:text-base"
                >
                  <div class="text-nowrap uppercase">
                    {{ formatMonthYear(event.cptCampusEvents?.eventDateStart) }}
                  </div>
                  <div class="text-xl font-black text-black uppercase md:text-[40px] md:leading-[115%]">
                    {{ formatDay(event.cptCampusEvents?.eventDateStart) }}
                  </div>
                  <div>
                    {{ formatWeekday(event.cptCampusEvents?.eventDateStart) }}
                  </div>
                </div>
              </div>

              <!-- <div class="bg-brand-700/[0.2] hidden w-[1px] md:block"></div> -->

              <!-- Content -->
              <div class="flex flex-col items-start gap-3 md:gap-5">
                <span
                  v-if="event.cptCampusEvents?.eventFormat?.nodes?.[0]?.name"
                  class="bg-accent-100 px-2.5 py-1.5 text-[13px] leading-[90%] lowercase"
                >
                  {{ event.cptCampusEvents.eventFormat.nodes[0].name }}
                </span>

                <div>
                  <TypographyTitle size="sm" as="h5" class="mb-4 md:mb-5">
                    {{ event.title }}
                  </TypographyTitle>

                  <div
                    class="border-accent-500 flex flex-col gap-3 border-l pl-6 text-xs leading-[165%] text-black/90 md:text-[13px]"
                  >
                    <div class="">
                      <!-- TIME LOGIC -->
                      <span v-if="event.cptCampusEvents?.allDayEvent && !event.cptCampusEvents?.eventTimeStart">
                        All Day Event
                      </span>

                      <span v-else-if="event.cptCampusEvents?.eventTimeStart">
                        {{ event.cptCampusEvents.eventTimeStart
                        }}<template v-if="event.cptCampusEvents.eventTimeEnd"
                          >&nbsp;–&nbsp;{{ event.cptCampusEvents.eventTimeEnd }}</template
                        >
                      </span>
                    </div>

                    <!-- Location and Address if available -->
                    <div v-if="event.cptCampusEvents?.eventLocationName">
                      {{ event.cptCampusEvents.eventLocationName }}
                    </div>

                    <a
                      v-if="event.eventGoogleMapsLink"
                      :href="event.eventGoogleMapsLink"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="text-brand-600 hover:text-brand-300 block transition-colors"
                    >
                      <div class="text-xs leading-tight">
                        {{ event.cptCampusEvents?.eventAddress1
                        }}<span v-if="event.cptCampusEvents?.eventAddress2"
                          >, {{ event.cptCampusEvents.eventAddress2 }}</span
                        >
                        <span v-if="event.cptCampusEvents?.eventCity">, {{ event.cptCampusEvents.eventCity }} </span>
                        <span v-if="event.cptCampusEvents?.eventState">, {{ event.cptCampusEvents.eventState }}</span>
                        <span v-if="event.cptCampusEvents?.eventZip">, {{ event.cptCampusEvents.eventZip }}</span>
                      </div>
                    </a>
                  </div>
                </div>

                <div
                  :key="`desc-${event.id}`"
                  class="event-description flex max-w-[900px] flex-col gap-3 text-xs leading-[165%] text-black md:text-base md:leading-[175%]"
                  v-html="event.cptCampusEvents?.eventDescription"
                />

                <div class="flex flex-col gap-3 md:flex-row md:gap-4">
                  <BaseButton
                    v-if="event.cptCampusEvents?.eventRegistrationLink"
                    variant="outline"
                    class="!px-3.5 !py-2 !text-[15px] !font-medium"
                    :href="event.cptCampusEvents.eventRegistrationLink"
                  >
                    Register
                  </BaseButton>

                  <!-- TODO: FUTURE FEATURE -->
                  <!-- <BaseButton variant="primary"> Add to Calendar </BaseButton> -->
                </div>
              </div>
            </div>
          </div>

          <!-- LOAD MORE button -->
          <div v-if="hasMoreEvents" class="mt-8 flex justify-center">
            <BaseButton variant="primary" class="!px-6 !py-3 !text-[15px] !font-medium" @click="loadMore">
              Load More
            </BaseButton>
          </div>
        </div>
      </div>
    </LayoutsContainer>
  </div>
</template>

<style scoped>
:deep(.hero-teaser p) {
  margin: 0;
  font-size: inherit;
  line-height: inherit;
  font-weight: inherit;
  color: inherit;
}

:deep(.event-description p) {
  margin: 0;
  font-size: inherit;
  line-height: inherit;
  font-weight: inherit;
}

:deep(.event-description ul) {
  list-style: disc;
  padding-left: 20px;
}

:deep(.event-description > ul li) {
  font-weight: 400 !important;
  font-size: inherit;
}
</style>
